import { Preset, SelectablePresets } from '../../../../widgets/selectablePresets';
import React, { FC, useCallback, useMemo } from 'react';
import { toLocalID } from 'src/shared/helpers';
import styled from 'styled-components';
import { PaletteColorInput } from '../__generated__/paletteColorsPageUpdateMutation.graphql';

interface PantoneLibraryColorsProps {
    onSelectedColorsChange: (colors: PaletteColorInput[]) => void;
    currentColors: PaletteColorInput[];
    singleSelection?: boolean;
    initialSearchValue?: string; // Optional initial search value
    selectedColors?: PaletteColorInput[];
}

const StyledSelectablePresets = styled(SelectablePresets)`
    padding-left: 5px;
`;

export const PantoneLibraryContent: FC<PantoneLibraryColorsProps> = ({
    onSelectedColorsChange,
    currentColors,
    singleSelection = false,
    selectedColors = []
}) => {
    // Map pattern presets to MaterialPreset interface
    const mappedColors: Preset[] = currentColors.map((color) => ({
        presetID: toLocalID(color.id) || 0,
        name: color.name || '',
        otherName: color.spotColorName || '',
        rgbHexCode: color.rgbHexCode || ''
    }));

    // Handle single color selection
    const handleSingleColorSelect = useCallback(
        (presetID: number) => {
            const newColor = currentColors.find((color) => toLocalID(color.id) === presetID);
            if (newColor) {
                onSelectedColorsChange([newColor]);
            }
        },
        [currentColors, onSelectedColorsChange]
    );

    // Handle multiple color selection
    const handleMultipleColorsSelect = useCallback(
        (presetIDs: number[]) => {
            // Get colors from the current palette that are in the selection
            const currentPaletteSelectedColors = currentColors.filter((color) => presetIDs.includes(toLocalID(color.id) || 0));

            // Get the IDs of colors from the current palette
            const currentPaletteColorIDs = currentColors.map((color) => toLocalID(color.id) || 0);

            // Keep colors from other palettes (colors that are not in the current palette)
            const colorsFromOtherPalettes = selectedColors.filter((color) => !currentPaletteColorIDs.includes(toLocalID(color.id) || 0));

            // Merge: colors from other palettes + selected colors from current palette
            onSelectedColorsChange([...colorsFromOtherPalettes, ...currentPaletteSelectedColors]);
        },
        [currentColors, selectedColors, onSelectedColorsChange]
    );

    // Get the selected values based on selection mode
    // Filter to only show colors that are visible in the current palette
    const currentPaletteColorIDs = useMemo(() => currentColors.map((color) => toLocalID(color.id) || 0), [currentColors]);

    const selectedColorIDs: number[] = useMemo(() => {
        // Get all selected color IDs
        const allSelectedIDs = selectedColors.map((color) => toLocalID(color.id) || 0);
        // Only show the ones that are in the current palette view
        return allSelectedIDs.filter((id) => currentPaletteColorIDs.includes(id));
    }, [selectedColors, currentPaletteColorIDs]);

    const selectedColorID: number | null = useMemo(() => (singleSelection ? selectedColorIDs[0] || null : null), [singleSelection, selectedColorIDs]);

    // Memoize the static props to prevent unnecessary re-renders
    const staticPresetProps = useMemo(
        () => ({
            columnWidthPreset: 137,
            gapPreset: '16px 24px' as const,
            hideUpload: true,
            showCaption: false,
            isImageChild: false
        }),
        []
    );

    // Memoize the SelectablePresets component
    const selectablePresetsProps = useMemo(
        () => ({
            presets: mappedColors,
            onPresetSelect: handleSingleColorSelect,
            onPresetsSelect: handleMultipleColorsSelect,
            multiSelect: !singleSelection,
            ...staticPresetProps,
            // Use the appropriate prop based on selection mode
            ...(!singleSelection ? { selectedPresetIDs: selectedColorIDs } : { selectedPresetID: selectedColorID })
        }),
        [mappedColors, handleSingleColorSelect, handleMultipleColorsSelect, singleSelection, selectedColorIDs, selectedColorID, staticPresetProps]
    );

    return <StyledSelectablePresets {...selectablePresetsProps} />;
};
